import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import appConfig from '@/config/appConfig';

/**
 * EmbedApp Component
 * 
 * Allows embedding individual apps on external websites
 * 
 * Usage:
 * <iframe src="https://yourdomain.com/embed/bubble-wrap" width="800" height="600"></iframe>
 * 
 * Available apps:
 * /embed/bubble-wrap
 * /embed/liquid
 * /embed/noise
 * /embed/reflex
 * /embed/animals
 * ... (all app IDs from appConfig)
 */

const EmbedApp = () => {
  const { appId } = useParams();
  const app = appConfig.find(a => a.id === appId);

  useEffect(() => {
    // Remove navigation and extra UI for embedded view
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  if (!app) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#E0F7FA] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-[#2D3748] mb-4">App Not Found</h2>
          <p className="text-[#4A5568]">The app "{appId}" does not exist.</p>
        </div>
      </div>
    );
  }

  // Check if app is premium (for embedded view, show free preview only)
  if (app.isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] to-[#E0F7FA] flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-3xl p-8 shadow-2xl border border-slate-100 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-9 h-9 text-yellow-900" strokeWidth={1.6} />
          </div>
          
          <h2 className="text-3xl font-bold text-[#2D3748] mb-4">{app.name}</h2>
          <p className="text-lg text-[#4A5568] mb-6">{app.description}</p>
          
          <p className="text-[#4A5568] mb-6">
            This is a premium app. Visit the full platform to unlock access!
          </p>
          
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-8 py-4 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg"
          >
            Try Inclusion Apps
          </a>
        </div>
      </div>
    );
  }

  // Render the app component directly (no auth required for embeds)
  const AppComponent = app.component;
  
  return (
    <div className="embed-container" style={{ width: '100%', height: '100vh' }}>
      <AppComponent />
      
      {/* Watermark */}
      <div className="fixed bottom-4 right-4 z-50">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg text-sm font-semibold text-[#2D3748] hover:bg-white transition-all"
        >
          Powered by Inclusion Apps
        </a>
      </div>
    </div>
  );
};

export default EmbedApp;
